import {
    CloseOutlined,
    EmailOutlined,
    Facebook,
    Favorite,
    Instagram,
    Inventory2Outlined,
    Language,
    LinkedIn,
    PersonOutlineOutlined,
    Phone,
    X,
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import { ReactComponent as Export } from 'assets/svg/export-request.svg';
import { useUpdatePropertyRequest } from 'hooks/usePropertyRequests';
import { useScrollLock } from 'hooks/useScrollLock';
import { useState } from 'react';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { formatDateForPropertyRequests } from 'util/formatDate';
import { urgencyOptions } from 'util/urgencySelector';
import DeleteRequestModal from './DeleteRequestModal';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isShowArchiveIcon?: boolean;
}

export default function OpenRequestDetailsModal({ isOpen, setIsOpen, isShowArchiveIcon = true }: Props) {
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const { lockScroll, unlockScroll } = useScrollLock();
    const { selectedLead } = usePropertyRequestsStore();
    const { mutate } = useUpdatePropertyRequest();

    if (isOpen) {
        lockScroll();
    } else {
        unlockScroll();
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-50 font-poppins">
                <div
                    className={`fixed right-0 top-0 h-full w-full max-w-4xl overflow-auto bg-white/100 z-50 motion-safe:animate-[slideLeft_400ms_ease-out_forwards]`}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-3 border-b sticky top-0 bg-white z-10">
                        <div className="flex gap-3 items-center">
                            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <CloseOutlined className="h-5 w-5" />
                            </button>
                            <h2 className="text-lg text-my-black/80 font-semibold">REQ-20231126-07</h2>
                        </div>
                        {isShowArchiveIcon && (
                            <button onClick={() => setIsArchiveModalOpen(true)}>
                                <Inventory2Outlined className="text-my-red hover:text-my-red/80 cursor-pointer" />
                            </button>
                        )}
                    </div>

                    <div className="px-9 py-6 bg-my-gray/20 m-3 rounded-lg flex gap-1 items-center">
                        <Favorite className={` ${isShowArchiveIcon ? 'text-my-green' : 'text-my-gray/70'}`} />
                        <h5 className="ml-1">
                            {!isShowArchiveIcon && 'Previously '}
                            Saved by {selectedLead.savedCount} agents
                        </h5>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        <span className="text-my-black/60">Posted {formatDateForPropertyRequests(selectedLead?.createdAt)}</span>
                        <h3 className="text-lg font-semibold text-my-black/80 ">{selectedLead?.title}</h3>
                        <p className="text-my-black/80 font-normal text-base">{selectedLead?.description}</p>
                        <Divider className="!mt-8" />

                        <div className="!mt-8">
                            <h4 className="font-semibold mb-4 text-my-black/80 ">Request Details</h4>
                            <div className="flex flex-col gap-2.5 mb-8">
                                {selectedLead?.definedArea ? (
                                    <Details title="Area" value={selectedLead?.definedArea} />
                                ) : (
                                    <>
                                        <Details title="Country" value={selectedLead?.propertyCountry} />
                                        <Details title="State" value={selectedLead?.states?.map((state) => state.state).join(', ')} />
                                        <Details title="City" value={selectedLead?.cities.map((city) => city.city).join(', ')} />
                                        {selectedLead?.propertyNeighborhood && selectedLead?.propertyNeighborhood.length > 0 && (
                                            <Details title="Neighborhood" value={selectedLead?.propertyNeighborhood.join(', ')} />
                                        )}
                                    </>
                                )}

                                {selectedLead?.minBudget && selectedLead?.maxBudget && (
                                    <Details title="Budget" value={`€${selectedLead?.minBudget} - €${selectedLead?.maxBudget}`} />
                                )}
                                {selectedLead?.minConstructionArea && selectedLead?.maxConstructionArea && (
                                    <Details
                                        title="Constructed area"
                                        value={`${selectedLead?.minConstructionArea} - ${selectedLead?.maxConstructionArea} m²`}
                                    />
                                )}
                                {selectedLead?.minPlotArea && selectedLead?.maxPlotArea && (
                                    <Details title="Plot area" value={`${selectedLead?.minPlotArea} - ${selectedLead?.maxPlotArea} m²`} />
                                )}
                                {selectedLead?.minBedrooms && selectedLead?.maxBedrooms && (
                                    <Details title="Approx. Bedrooms" value={`${selectedLead?.minBedrooms} - ${selectedLead?.maxBedrooms}`} />
                                )}
                                {selectedLead?.minBathrooms && selectedLead?.maxBathrooms && (
                                    <Details title="Approx. Bathrooms" value={`${selectedLead?.minBathrooms} - ${selectedLead?.maxBathrooms}`} />
                                )}
                                <Details title="Urgency" value={urgencyOptions.find((option) => option.value === selectedLead?.urgency)?.label!} />
                                {selectedLead?.furnishing && <Details title="Furnishing" value={selectedLead?.furnishing} />}
                                <Details title="Agent Commission" value={`${selectedLead?.commission}%`} />
                                {selectedLead?.buyerProfile && <Details title="Buyer Profile" value={selectedLead?.buyerProfile} />}
                            </div>
                        </div>

                        {/* Amenities */}
                        <div>
                            <h4 className="font-medium mb-2 text-my-black/60 ">Amenities</h4>
                            <p className="text-my-black/90 font-normal text-base">
                                {selectedLead?.amenities
                                    .split(',')
                                    .map((item) => item.trim())
                                    .join(', ')}
                            </p>
                        </div>

                        {/* Requirements */}
                        <div>
                            <h4 className="font-medium mb-2 text-my-black/60 ">Other Specific Requirements</h4>
                            <p className="text-my-black/90 font-normal text-base">{selectedLead?.otherSpecificRequirements}</p>
                        </div>
                        <Divider className="!mt-8" />

                        {/* Contact Information */}
                        <div className="!mt-8 mb-6 ">
                            <h2 className="text-xl font-semibold mb-6">Provided Contact Information</h2>
                            <div className="space-y-1 grid md:grid-cols-2 gap-2">
                                {selectedLead?.contactInfo[0].name && (
                                    <ContactItem Icon={PersonOutlineOutlined} label="Name" value={selectedLead?.contactInfo[0].name} />
                                )}
                                {selectedLead?.contactInfo[0].email && (
                                    <ContactItem Icon={EmailOutlined} label="Email" value={selectedLead?.contactInfo[0].email} />
                                )}
                                {selectedLead?.contactInfo[0].phone && (
                                    <ContactItem Icon={Phone} label="Phone Number" value={selectedLead?.contactInfo[0].phone} />
                                )}
                                {selectedLead?.contactInfo[0].website && (
                                    <ContactItem Icon={Language} label="Website" value={selectedLead?.contactInfo[0].website} />
                                )}
                                {selectedLead?.contactInfo[0].facebook && (
                                    <ContactItem Icon={Facebook} label="Facebook" value={selectedLead?.contactInfo[0].facebook} />
                                )}
                                {selectedLead?.contactInfo[0].instagram && (
                                    <ContactItem Icon={Instagram} label="Instagram" value={selectedLead?.contactInfo[0].instagram} />
                                )}
                                {selectedLead?.contactInfo[0].linkedin && (
                                    <ContactItem Icon={LinkedIn} label="LinkedIn" value={selectedLead?.contactInfo[0].linkedin} />
                                )}
                                {selectedLead?.contactInfo[0].twitter && <ContactItem Icon={X} label="X" value={selectedLead?.contactInfo[0].twitter} />}
                                {selectedLead?.contactInfo[0].buyerIdentification && (
                                    <ContactItem
                                        Icon={PersonOutlineOutlined}
                                        label="Buyer Identification"
                                        value={selectedLead?.contactInfo[0].buyerIdentification}
                                    />
                                )}
                            </div>
                        </div>
                        {/*  */}
                    </div>
                </div>
            </div>
            <DeleteRequestModal
                title="Archive Request Post"
                description="This request will be removed from your feed and moved to the archive. You can restore it anytime if needed."
                Icon={Export}
                visible={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                onRemove={() => {
                    setIsArchiveModalOpen(false);
                    setIsOpen(false);
                    mutate({ propertyLeadId: selectedLead?.id, isArchived: true });
                }}
            />
        </>
    );
}

interface ContactItem {
    Icon: React.ElementType<any, keyof React.JSX.IntrinsicElements>;
    label: string;
    value: string;
}

function ContactItem({ Icon, label, value }: ContactItem) {
    return (
        <div className="flex flex-col gap-2 py-2">
            <span className="text-my-black/80 font-semibold text-base ">{label}</span>
            <div className="flex items-center gap-3 ">
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-my-black/80">{value}</span>
            </div>
        </div>
    );
}

function Details({ title, value }: { title: string; value: string }) {
    return (
        <div className=" flex justify-between">
            <div className="text-my-black/50 text-base mb-1">{title}</div>
            <div className="font-medium text-my-black/80">{value}</div>
        </div>
    );
}
